import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { trackSrc } from "@/lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    a.volume = volume;
    audioRef.current = a;
    const onTime = () => {
      const t = a.currentTime || 0;
      setProgress(t);
      // Preview cap for paid tracks
      const cur = currentRef.current;

    };
    const onDur = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setIsPlaying(false);
      if (queueRef.current.length > 0) {
        const [n, ...rest] = queueRef.current;
        setQueue(rest);
        playTrackInternal(n, rest);
      }
    };
    let retries = 0;
    const onErr = () => {
      const cur = currentRef.current;
      if (!cur || retries >= 2) { setIsPlaying(false); return; }
      retries += 1;
      const wasAt = a.currentTime || 0;
      setTimeout(() => {
        if (currentRef.current?.id !== cur.id) return;
        const base = trackSrc(cur);
        a.src = base + (base.includes("?") ? "&" : "?") + `retry=${retries}`;
        a.currentTime = wasAt;
        a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      }, 800 * retries);
    };
    a.addEventListener("error", onErr);
    a.addEventListener("loadstart", () => { if (!a.src.includes("retry=")) retries = 0; });
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line
  }, []);

  const currentRef = useRef(null);
  const queueRef = useRef([]);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const playTrackInternal = (track, newQueue = []) => {
    if (!track || !audioRef.current) return;
    const a = audioRef.current;
    a.src = trackSrc(track);
    a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    setCurrent(track);
    if (newQueue.length) setQueue(newQueue);
    prefetch(newQueue[0]);
  };

  // Warm the server-side cache (and the browser's) so the next play starts instantly
  const prefetched = useRef(new Set());
  const prefetch = useCallback((track) => {
    if (!track) return;
    const url = trackSrc(track);
    if (prefetched.current.has(url)) return;
    prefetched.current.add(url);
    fetch(url + (url.includes("?") ? "&" : "?") + "count=false", { headers: { Range: "bytes=0-1" }, credentials: "include" }).catch(() => {});
  }, []);

  const playTrack = useCallback((track, newQueue = []) => {
    if (!track || !audioRef.current) return;
    const a = audioRef.current;
    if (currentRef.current?.id === track.id) {
      if (a.paused) {
        a.play().catch(() => {});
        setIsPlaying(true);
      } else {
        a.pause();
        setIsPlaying(false);
      }
      return;
    }
    playTrackInternal(track, newQueue);
  }, []);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a || !currentRef.current) return;
    if (a.paused) {
      a.play().catch(() => {});
      setIsPlaying(true);
    } else {
      a.pause();
      setIsPlaying(false);
    }
  }, []);

  const seek = useCallback((t) => {
    if (!audioRef.current || !Number.isFinite(t) || t < 0) return;
    const cur = currentRef.current;
    audioRef.current.currentTime = t;
  }, []);

  const next = useCallback(() => {
    if (queueRef.current.length > 0) {
      const [n, ...rest] = queueRef.current;
      setQueue(rest);
      playTrackInternal(n, rest);
    }
  }, []);

  return (
    <PlayerContext.Provider value={{
      current, isPlaying, progress, duration, volume,
      setVolume, playTrack, togglePlay, seek, next, prefetch,
      previewSec: null,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
